import { pick } from 'underscore';

import deleteBackground from '@shoflo/studio-redux/actions/background/deleteBackground';
import deleteBanner from '@shoflo/studio-redux/actions/banner/deleteBanner';
import batchUpdateScenes from '@shoflo/studio-redux/actions/scene/utils/batchUpdateScenes';

import deleteSource from 'actions/Broadcast/sources/deleteSource';
import {
  getSourceValue,
  filteredScenesForUpdate
} from 'utils/broadcastSourceHelper';
import { AUDIO_PRESENTER } from '@shoflo/polymer/constants/SourceItems';
import { withCurrentParams } from 'actions/utils';
import { prepareSceneForUpdate } from '@studio/program/utils/scene';
import { BROADCAST_REPLACE_SOURCE_FROM_SCENE } from 'actions/Types';
import setProgram from 'actions/Broadcast/program/setProgram';

/**
 * Replace source in scenes
 *
 * @param {string} sourceId - id of the source to replace
 * @param {Object} replacementSource - the source to replace with
 * @param {Array} scenes - an array of scenes to perform replacement on
 * @param {Object} deleteParams - extra parameters
 */

export default (sourceId, replacementSource, scenes, deleteParams = {}) => {
  return withCurrentParams(
    (dispatch, { teamId, eventId, broadcastId }, getState) => {
      const { backgrounds, overlays, banners, brandingSettings } = getState();
      const filteredReplacementSource = pick(replacementSource, [
        '_id',
        'hidden',
        'title',
        'type',
        'value',
        'order',
        'thumbnail'
      ]);
      scenes.forEach((scene, index) => {
        if (!deleteParams.isBackgroundSource) {
          let overlay, banner;
          if (deleteParams.isOverlaySource) {
            overlay = overlays.find(
              o => o.source._id === replacementSource._id
            );
          }
          if (deleteParams.isBannerSource) {
            banner = banners.find(b => b.source._id === replacementSource._id);
          }

          const isReplacementInScene = scene.layers.find(
            layer =>
              layer.source._id === filteredReplacementSource._id ||
              (overlay && layer._id === overlay?._id) ||
              (banner && layer._id === banner?._id)
          );
          scene.layers = scene.layers.map(layer => {
            // layer._id within the condition handles the case where source
            // is an overlay or a banner then the layer._id is the id of the overlay or banner
            if (
              (layer.source._id === sourceId || layer._id === sourceId) &&
              !isReplacementInScene
            ) {
              layer._id = filteredReplacementSource._id;
              layer.source = filteredReplacementSource;
              layer.source.hidden = false;
              if (overlay) {
                layer._id = overlay?._id;
              } else if (banner) {
                layer._id = banner?._id;
              }
            }
            // Mute state of the deleted source
            if (
              layer.source.value === sourceId &&
              layer.source.type === AUDIO_PRESENTER
            ) {
              const sourceValue = getSourceValue(filteredReplacementSource);
              layer._id = sourceValue;
              layer.source = {
                ...filteredReplacementSource,
                _id: sourceValue,
                value: filteredReplacementSource._id,
                type: AUDIO_PRESENTER
              };
            }
            return layer;
          });
          // Remove source if replacement was not done;
          if (isReplacementInScene) {
            // layer._id within the condition handles the case where source is an overlay,
            // then the layer._id is the id of the overlay
            scene.layers = scene.layers.filter(
              layer =>
                layer.source._id !== sourceId &&
                layer._id !== sourceId &&
                layer.source.value !== sourceId
            );
          }
          scenes[index] = prepareSceneForUpdate(
            JSON.parse(JSON.stringify(scene)),
            brandingSettings?.fonts
          );
        } else {
          const { source, _id } = backgrounds.find(
            b => b.source._id === replacementSource._id
          );
          scene.style = {
            ...scene.style,
            backgroundId: _id,
            backgroundImage: `url(${source.value.url})`
          };
        }
      });
      const filteredScenes = filteredScenesForUpdate(scenes);
      const action = {
        type: BROADCAST_REPLACE_SOURCE_FROM_SCENE,
        request: async apiClient => {
          const response = await batchUpdateScenes(filteredScenes, apiClient, {
            teamId,
            eventId,
            broadcastId
          });

          if (deleteParams.isBackgroundSource) {
            dispatch(
              deleteBackground(sourceId, {
                replacementSource,
                isBackgroundSource: true
              })
            );
          } else if (deleteParams.isBannerSource) {
            dispatch(
              deleteBanner(
                sourceId,
                {
                  replacementSource,
                  isBannerSource: true
                },
                setProgram
              )
            );
          } else {
            dispatch(deleteSource(sourceId, { replacementSource }));
          }
          return { scenes: response.scenes };
        }
      };
      dispatch(action);
    }
  );
};
